// src/pages/battle/BattleTopPage.jsx
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useBattleTop } from "../../hooks/BattleUseTop";
import { useJWT } from "../../contexts/JWTContext"; // 🔥 JWT 공통 룰
import BattlePageHeader from "../../components/battle/BattlePageHeader"; // ✅ 공통 헤더


const GENDER_TABS = [
  { value: "F", label: "여자 랭커" },
  { value: "M", label: "남자 랭커" },
];

// 🔥 레벨 아이콘 파일 매핑 (회원 화면과 동일 룰)
const LEVEL_ICON_MAP = {
  1: "level_1",
  2: "level_2",
  3: "level_3",
  4: "level_4",
  5: "level_5",
  6: "level_6",
  7: "level_7",
  8: "level_8",
  9: "level_9",
  10: "level_10",
};

// 🔹 승수 기준으로 공동 순위(DENSE RANK) 계산
function withRanksByWinCount(list) {
  if (!Array.isArray(list)) return [];

  // 혹시 몰라서 승수 기준 내림차순 정렬
  const sorted = [...list].sort((a, b) => b.winCount - a.winCount);

  let prevWin = null;
  let currentRank = 0;

  return sorted.map((item) => {
    if (item.winCount !== prevWin) {
      // 승수가 바뀌면 다음 순위
      currentRank += 1;
      prevWin = item.winCount;
    }
    return { ...item, rank: currentRank };
  });
}

export default function BattleTopPage() {
  const navigate = useNavigate();

  // 🔥 JWT 공통 사용 (지금은 직접 안 써도 패턴 유지)
  const jwt = useJWT();
  const loginUserId = jwt?.userId || null;
  const token = jwt?.token || null;

  const { maleRankers, femaleRankers, loading, error } = useBattleTop();
  const [activeGender, setActiveGender] = useState("F");

  const filteredList = useMemo(() => {
    const rawList = activeGender === "F" ? femaleRankers : maleRankers;
    return withRanksByWinCount(rawList);
  }, [activeGender, maleRankers, femaleRankers]);

  const handleBack = () => {
    navigate(-1);
  };

  return (
<div className="min-h-screen bg-slate-50 flex justify-center">
      {/* ✅ 모바일 컬럼 (절대 제거 X) */}
      <div className="w-full max-w-md flex flex-col">

        {/* ✅ 헤더 : 스크롤 영역 밖 → 고정 효과 */}
        <BattlePageHeader
          title="대결의 전당"
          showBack
          backButtonProps={{ onClick: handleBack }}
        />

        {/* ✅ 스크롤 영역 (핵심) */}
        <div className="flex-1 overflow-y-auto px-4 py-3">

          {/* 설명 카드 */}
          <section className="mb-4 rounded-2xl bg-white/70 backdrop-blur shadow-sm px-4 py-3">
            <h2 className="font-semibold text-sm mb-1">WINGSTEP RANK</h2>
            <p className="text-[11px] text-slate-500 leading-snug">
              대결에서 승리한 횟수가 많은 러너 TOP 10 소개합니다.
            </p>
          </section>

          {/* 성별 탭 */}
          <GenderTabs activeGender={activeGender} onChange={setActiveGender} />

          {/* 리스트 영역 */}
          <section className="mt-3 space-y-3">
            {loading && (
              <p className="text-xs text-slate-400 px-2">랭킹 불러오는 중...</p>
            )}

            {error && !loading && (
              <p className="text-xs text-red-400 px-2">
                랭킹을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.
              </p>
            )}

            {!loading && !error && filteredList.length === 0 && (
              <p className="text-xs text-slate-400 px-2">
                아직 랭킹 데이터가 없어요.
              </p>
            )}

            {!loading &&
              !error &&
              filteredList.map((runner) => (
                <RankCard
                  key={`${runner.rank}-${runner.userId || runner.nickname}`}
                  runner={runner}
                />
              ))}
                <div className="h-28" />
          </section>
        </div>
      </div>
      
    </div>
  );
}

/** 성별 탭 */
function GenderTabs({ activeGender, onChange }) {
  return (
    <div className="flex items-center justify-between px-2 mt-2 mb-1">
      <div className="inline-flex p-1 rounded-full bg-slate-100">
        {GENDER_TABS.map((tab) => {
          const isActive = activeGender === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => onChange(tab.value)}
              className={`px-4 py-1 rounded-full text-xs font-medium transition
                ${
                  isActive
                    ? "bg-white shadow-sm text-slate-900"
                    : "text-slate-400"
                }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      
    </div>
    
  );
}

/** 랭킹 카드 */
function RankCard({ runner }) {
  const { rank, avatarUrl, nickname, level, winCount } = runner;
  const isFirst = rank === 1;

  // 🔹 1,2,3등 메달 색상
  const medalBgMap = {
    1: "bg-amber-400", // 금
    2: "bg-slate-300", // 은
    3: "bg-amber-700", // 동
  };

  const medalTextColorMap = {
    1: "text-white",
    2: "text-slate-800",
    3: "text-white",
  };

  // 레벨 아이콘
  const levelIconSrc = `/images/levelIcon/level_${level || 1}.png`;

  // 아바타
  const avatarSrc = avatarUrl
    ? `/images/avatar/${avatarUrl}`
    : "/images/avatar/avatar_basic.png";

  return (
    <article
      className={`flex items-center rounded-2xl px-5 py-4 bg-white/90 backdrop-blur shadow-sm border transition
      ${isFirst ? "border-amber-300 bg-amber-50" : "border-slate-100"}`}
    >
      {/* 왼쪽 순위 영역 */}
      <div className="mr-4 flex flex-col items-center min-w-[40px]">
        {rank <= 3 ? (
          <div className="flex flex-col items-center">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold 
              ${medalBgMap[rank]} ${medalTextColorMap[rank]}`}
            >
              {rank}
            </div>
            <div className={`w-4 h-3 ${medalBgMap[rank]} rounded-b-full`} />
          </div>
        ) : (
          <div className="w-9 h-9 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-sm font-bold">
            {rank}
          </div>
        )}

        {rank === 1 && (
          <span className="mt-1 text-[10px] text-amber-500 font-semibold"></span>
        )}
      </div>

      {/* 아바타 */}
      <div className="mr-3">
        <img
          src={avatarSrc}
          alt={`${nickname} avatar`}
          className="w-16 h-16 rounded-full border border-slate-200 object-cover"
        />
      </div>

      {/* 닉네임 + 레벨 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <img
            src={levelIconSrc}
            alt="레벨 아이콘"
            className="w-8 h-8 object-contain"
          />
          <p className="font-semibold text-sm truncate">{nickname}</p>
        </div>

        {/* 승수 */}
        <p className="text-[12px] text-slate-500">승리 {winCount}회</p>
      </div>
    </article>
  );
}
